import React from 'react';
import { Calendar, Tag, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Blog = () => {
  // Articles de blog statiques avec contenu SEO optimisé
  const staticBlogPosts = [
    {
      id: 'transferts-aeroport-premium-elitetransfert',
      title: 'Transferts Aéroport Premium : L\'Excellence Selon Elite Transfert',
      excerpt: 'Découvrez comment nos services de transfert aéroport premium transforment votre expérience de voyage dès votre arrivée. Confort, ponctualité et service personnalisé pour tous vos déplacements.',
      image: '/images/blog/airport-guide.jpg',
      category: 'Services Premium',
      date: '2 mars 2025',
      tags: ['Transfert Aéroport', 'Service VTC', 'Voyage d\'Affaires', 'Confort Premium'],
      readTime: '7 min'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Blog Elite Transfert | Articles sur le Transport VTC Premium</title>
        <meta name="description" content="Découvrez nos articles sur le transport VTC premium, les transferts aéroport et les services de chauffeur privé pour vos déplacements professionnels et personnels." />
        <meta name="keywords" content="blog vtc, transport premium, chauffeur privé, transfert aéroport, vtc paris" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-[#E5C13B] mb-4">Notre Blog</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Découvrez nos articles et conseils sur le transport VTC premium, les transferts aéroport 
            et les services de chauffeur privé pour vos déplacements professionnels et personnels.
          </p>
        </div>

        {staticBlogPosts.length > 0 ? (
          <>
            {/* Article principal mis en avant */}
            {staticBlogPosts[0] && (
              <div className="mb-16">
                <Link 
                  to={`/blog/${staticBlogPosts[0].id}`}
                  className="group block overflow-hidden rounded-2xl bg-gray-900 shadow-xl transition-all duration-300 hover:shadow-2xl border border-gray-800 hover:border-[#E5C13B]"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    <div className="overflow-hidden">
                      <img
                        src={staticBlogPosts[0].image}
                        alt={staticBlogPosts[0].title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-4 w-4" />
                          <time>{staticBlogPosts[0].date}</time>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Tag className="h-4 w-4" />
                          <span>{staticBlogPosts[0].category}</span>
                        </div>
                      </div>
                      
                      <h2 className="text-3xl font-bold text-[#E5C13B] mb-4 group-hover:text-white transition-colors duration-200">
                        {staticBlogPosts[0].title}
                      </h2>
                      
                      <p className="text-gray-300 mb-6 text-lg">
                        {staticBlogPosts[0].excerpt}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-6">
                        {staticBlogPosts[0].tags.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-[#E5C13B]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="mt-auto flex items-center text-[#E5C13B] group-hover:text-white transition-colors duration-200">
                        <span className="text-base font-medium">Lire l'article complet</span>
                        <ChevronRight className="h-5 w-5 ml-2" />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Grille d'articles secondaires */}
            {staticBlogPosts.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {staticBlogPosts.slice(1).map(post => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.id}`}
                    className="group bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-800 hover:border-[#E5C13B] flex flex-col h-full"
                  >
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-4">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-4 w-4" />
                          <time>{post.date}</time>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Tag className="h-4 w-4" />
                          <span>{post.category}</span>
                        </div>
                      </div>

                      <h2 className="text-xl font-semibold text-[#E5C13B] mb-3 group-hover:text-white transition-colors duration-200">
                        {post.title}
                      </h2>

                      <p className="text-gray-400 mb-4 text-sm flex-grow">
                        {post.excerpt}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-[#E5C13B]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className="mt-auto flex items-center text-[#E5C13B] group-hover:text-white transition-colors duration-200">
                        <span className="text-sm">Lire l'article</span>
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-xl text-gray-300">
              Aucun article disponible pour le moment. Revenez bientôt pour découvrir notre contenu.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
